import { View } from "ui/atoms/view";
import { Text } from "react-native";
import { Control, Controller } from "react-hook-form";
import { CreateCalendarEventForm } from "app/services/api/types/calendar/requests/CreateCalendarEventForm";
import { FormControlledInput } from "ui/molecules/form/FormControlledInput";
import { SelectableOptionWithControl } from "../molecules/SelectableOptionWithControl";
import { CheckboxGroup } from "ui/gluestack/checkbox";
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
} from "ui/gluestack/form-control";
import { AlertCircleIcon } from "ui/gluestack/icon";
import { requiredValidation } from "ui/utils/Validators";
import { FormControlledDropdown } from "ui/molecules/form/FormControlledDropdown";
import { DAYORDINALS, MONTHS, WEEKDAYS } from "app/constants/dateConstants";
import { format, getMonth } from "date-fns";
interface Props {
  control: Control<CreateCalendarEventForm>;
}
export function YearlyRecurrenceOptions({ control }: Props) {
  const thisMonthIndex = getMonth(new Date()) + 1;
  const thisMonthLabel =
    MONTHS.find((m) => m.value === String(thisMonthIndex))?.label ??
    "Select month";

  const today = format(new Date(), "do MMMM");

  return (
    <Controller
      control={control}
      name="recurrence_rule.yearly.option"
      render={({ field: { value, onChange }, fieldState: { error } }) => {
        return (
          <FormControl isInvalid={!!error}>
            <View className="flex-col gap-2">
              <FormControlledInput
                id="recurrence_rule.yearly.interval"
                name="recurrence_rule.yearly.interval"
                control={control}
                placeholder="0"
                className="flex-row items-center"
                textInputClassName="flex-1 max-w-12"
                isRequired
                prefix={
                  <Text className="text-sm text-gray-600 mr-2">Every</Text>
                }
                postfix={
                  <Text className="text-sm text-gray-600 ml-2">year(s)</Text>
                }
                rules={{
                  required: requiredValidation("Interval"),
                }}
              />
              <CheckboxGroup
                value={value ? [value] : []}
                onChange={(val) => {
                  onChange(val.length ? val[val.length - 1] : undefined);
                }}
                className="flex-col gap-4"
              >
                <SelectableOptionWithControl
                  value="specific_day_of_the_month"
                  controlElement={
                    <Text className="text-sm text-gray-600 mr-2">
                      On {thisMonthLabel} {today}
                    </Text>
                  }
                />
                <SelectableOptionWithControl
                  value="day_position_of_the_month"
                  controlElement={
                    <View className="flex-row items-center flex-1 flex-wrap gap-2">
                      <FormControlledDropdown
                        name="recurrence_rule.yearly.by_set_position"
                        control={control}
                        className="flex-row items-center flex-1 "
                        placeholder="First"
                        isDisabled={value !== "day_position_of_the_month"}
                        options={DAYORDINALS}
                        dropdownClassName={{ width: 100 }}
                        prefix={
                          <Text className="text-sm text-gray-600 mr-2">
                            On the
                          </Text>
                        }
                      />
                      <FormControlledDropdown
                        name="recurrence_rule.yearly.week_day"
                        control={control}
                        className="flex-row items-center flex-1 "
                        placeholder="Day"
                        isDisabled={value !== "day_position_of_the_month"}
                        options={WEEKDAYS}
                        dropdownClassName={{ width: 130 }}
                      />
                      <Text className="text-sm text-gray-600 mx-2">
                        of every {thisMonthLabel}
                      </Text>
                    </View>
                  }
                />
                <Controller
                  name="recurrence_rule.yearly"
                  control={control}
                  render={({ fieldState: { error } }) => {
                    return (
                      <FormControl isInvalid={!!error}>
                        {error?.message && (
                          <FormControlError>
                            <FormControlErrorIcon
                              as={AlertCircleIcon}
                              className="text-error-500"
                              size="sm"
                            />
                            <FormControlErrorText className="text-error-500 text-sm">
                              {error.message}
                            </FormControlErrorText>
                          </FormControlError>
                        )}
                      </FormControl>
                    );
                  }}
                  rules={{
                    validate: {
                      requiredDay: (value) => {
                        if (
                          value?.option === "day_position_of_the_month" &&
                          !value.week_day
                        ) {
                          return "Day is required";
                        }
                        return true;
                      },
                    },
                  }}
                />
                <FormControlError>
                  <FormControlErrorIcon
                    as={AlertCircleIcon}
                    className="text-error-500"
                    size="sm"
                  />
                  <FormControlErrorText className="text-error-500 text-sm">
                    {error?.message}
                  </FormControlErrorText>
                </FormControlError>
              </CheckboxGroup>
            </View>
          </FormControl>
        );
      }}
      rules={{
        required: requiredValidation("Yearly Interval"),
      }}
    />
  );
}
